import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ALERT_MODES, QuantumMetricEvents, regexPatterns, SEND_BP_FORM } from 'src/app/_constants/app.constants';
import { COUNTRY_CODES } from 'src/app/_constants/country_codes.constants';
import { ObjectLiteral } from 'src/app/_models/user';
import { BoardingPassService } from 'src/app/_services/api/boardingpass/boardingpass.service';
import { LanguageService } from 'src/app/_services/api/language/language.service';
import { LoggerService } from 'src/app/_services/ui/logger/logger.service';
import { FlightService } from 'src/app/_services/api/flight/flight.service';
import { AnalyticsService } from 'src/app/_services/sdk/analytics/analytics.service';

@Component({
  selector: 'ua-send-boardingpass-modal',
  templateUrl: './send-boardingpass-modal.component.html',
  styleUrls: ['./send-boardingpass-modal.component.scss']
})
export class SendBoardingpassModalComponent implements OnInit, OnDestroy {

  //language constants
  languageSubscription: Subscription
  rebookConstants: ObjectLiteral
  commonConstants: ObjectLiteral
  homeConstants: ObjectLiteral
  countryCodeConstants: ObjectLiteral

  //form constants
  defaultCountryCode = "USA +1"
  phone = SEND_BP_FORM.CONTROLS.PHONE
  email = SEND_BP_FORM.CONTROLS.EMAIL
  phoneCode = this.phone.controls.CODE
  phoneNumber = { ...this.phone.controls.NUMBER }

  //Subscriptions
  phoneCodeSubscription: Subscription

  @Output() modalDismiss = new EventEmitter()

  userForm = new FormGroup({
    email: new FormControl("", this.email.validators),
    code: new FormControl(this.defaultCountryCode, this.phoneCode.validators),
    number: new FormControl("", {
      validators: [...this.phoneNumber.validators.default, Validators.pattern(regexPatterns.PHONE_NUMBER_US)]
    })

  })

  get emailControl(): FormControl {
    return this.userForm.get('email') as FormControl
  }

  get phoneCodeControl(): FormControl {
    return this.userForm.get("code") as FormControl
  }

  get phoneControl(): FormControl {
    return this.userForm.get("number") as FormControl
  }

  constructor(
    private logger: LoggerService,
    private languageService: LanguageService,
    private boardingPassService: BoardingPassService,
    private flightService: FlightService,
    private analyticsService: AnalyticsService,
  ) {
    this.languageSubscription = this.languageService.languageChanges.subscribe(
      () => {
        this.rebookConstants = this.languageService.constants?.flightRebook || {}
        this.homeConstants = this.languageService.constants?.home || {}
        this.commonConstants = this.languageService.constants?.commons || {}
        this.countryCodeConstants = this.languageService.constants?.countryCodes || {}
      }
    )    
  }

  ngOnInit(): void {
    this.logger.debug("SendBoardingpassModalComponent.ngOnInit", this)
    this.configureFormValidators()
}

  closeModal() {
    this.logger.debug("SendBoardingpassModalComponent.closeModal", this)
    this.modalDismiss.emit()
  }

  configureFormValidators(): void {
    this.logger.debug("SendBoardingpassModalComponent.configureFormValidators", this)
    this.phoneCode.options = COUNTRY_CODES.map((country) => {
      return country.callingCodes.map(code => {
        return {
          label: `${this.countryCodeConstants[country.name]} +${code}`,
          value: `${this.countryCodeConstants[country.name]} +${code}`,
        }
      })
    }).reduce((prev, curr) => prev.concat(curr))
    this.phoneCodeSubscription = this.phoneCodeControl.valueChanges.subscribe((value) => {
      if (value !== this.defaultCountryCode) {
        this.phoneControl.setValidators([...this.phoneNumber.validators.others]);
      } else {
        this.phoneControl.setValidators([
          ...this.phoneNumber.validators.default,
          Validators.pattern(regexPatterns.PHONE_NUMBER_US),
        ])
      }
      this.phoneControl.setValue("")
    })
  }

  getCountryCode(callingCode: string): string {
    this.logger.debug("SendBoardingpassModalComponent.getCountryCode", this)
    if (!callingCode)
      return this.defaultCountryCode
    callingCode = callingCode.replace(/\D/g, "");
    let countryCodes = COUNTRY_CODES.filter(val => {
      return val.callingCodes.includes(callingCode)
    })
    if (countryCodes.length != 1) {
      return this.defaultCountryCode
    }
    return `${countryCodes[0].alpha3Code} +${callingCode}`
  }

  checkValidity() {
    return ((this.phoneControl.value && this.phoneControl.valid && this.emailControl.valid)
      || (this.emailControl.value && this.emailControl.valid && this.phoneControl.valid))
      ? false
      : true
  }

  async onFormSubmit() {
    this.logger.debug("SendBoardingpassModalComponent.onFormSubmit", this)
    await this.boardingPassService.sendBoardingPassDetails({
      languageCode: this.languageService.currentLanguage,
      countryCode: this.phoneCodeControl.value?.match(/\d+/g)?.toString(),
      optIn: "false",
      number: this.phoneControl.value?.match(/\d+/g)?.toString(),
      email: this.emailControl.value?.toString(),
      pnr: this.flightService.activeSegment.confirmationID,
      firstName: this.flightService.activeTraveler.givenName,
      lastName: this.flightService.activeTraveler.surname,
      passengerList: `${this.flightService.activeTraveler.surname}/${this.flightService.activeTraveler.givenName}`,
      segmentIdx: "01",
      legIdx: "01",
      eventID:this.generateGuid()
    }).then(() => {
      if (this.emailControl.value?.toString().length > 0) {
        this.analyticsService.sendEvent({
          eventId: QuantumMetricEvents.SENT_BOARDINGPASS_VIA_EMAIL,
          isConversion: 0,
          value: ''
        })
      } else if (this.phoneControl.value?.match(/\d+/g)?.toString().length > 0) {
        this.analyticsService.sendEvent({
          eventId: QuantumMetricEvents.SENT_BOARDINGPASS_VIA_SMS,
          isConversion: 0,
          value: ''
        })
      }
    });
    this.modalDismiss.emit(ALERT_MODES.SUCCESS)
  }

  ngOnDestroy() {
    this.logger.debug("SendBoardingpassModalComponent.ngOnDestroy", this)
    this.phoneCodeSubscription && this.phoneCodeSubscription.unsubscribe()
  }
  generateGuid () {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0,
        v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

}
